import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from '../services/api';

type FeedbackType = 'bug' | 'feature' | 'question' | 'other';

export function FeedbackButton() {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      let token: string | undefined;
      if (isAuthenticated) {
        token = await getAccessTokenSilently();
      }

      await api.submitFeedback({
        type: feedbackType,
        message: message.trim(),
        email: isAuthenticated ? user?.email : email,
        userAgent: navigator.userAgent,
        url: window.location.href,
      }, token);

      setSubmitted(true);
      setMessage('');

      // Auto-close after success
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const feedbackTypes: { value: FeedbackType; label: string; icon: string }[] = [
    { value: 'bug', label: 'Bug', icon: '🐛' },
    { value: 'feature', label: 'Feature', icon: '💡' },
    { value: 'question', label: 'Question', icon: '❓' },
    { value: 'other', label: 'Other', icon: '💬' },
  ];

  return (
    <>
      {/* Floating Feedback Button */}
      <button
        onClick={() => setIsOpen(true)}
        style={{ backgroundColor: '#2563eb' }}
        className="fixed bottom-6 right-6 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all z-40"
        title="Send Feedback"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </button>

      {/* Feedback Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div
            className="rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
            style={{ backgroundColor: '#ffffff' }}
          >
            {/* Header */}
            <div
              className="px-6 py-4 flex items-center justify-between"
              style={{ backgroundColor: '#2563eb' }}
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h2 className="text-lg font-semibold text-white">Send Feedback</h2>
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSubmitted(false);
                  setError(null);
                }}
                className="text-white hover:text-blue-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6" style={{ backgroundColor: '#ffffff' }}>
              {submitted ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#dcfce7' }}>
                    <svg className="w-8 h-8" style={{ color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-1" style={{ color: '#111827' }}>Thank You!</h3>
                  <p style={{ color: '#6b7280' }}>Your feedback has been submitted.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Feedback Type */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                      Type of feedback
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {feedbackTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFeedbackType(type.value)}
                          className="flex flex-col items-center gap-1 px-2 py-3 rounded-lg border-2 transition-all"
                          style={{
                            backgroundColor: feedbackType === type.value ? '#eff6ff' : '#ffffff',
                            borderColor: feedbackType === type.value ? '#2563eb' : '#e5e7eb',
                            color: feedbackType === type.value ? '#1d4ed8' : '#374151',
                          }}
                        >
                          <span className="text-lg">{type.icon}</span>
                          <span className="text-xs font-medium">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Email (for non-authenticated users) */}
                  {!isAuthenticated && (
                    <div>
                      <label htmlFor="feedback-email" className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>
                        Email <span style={{ color: '#9ca3af' }}>(optional)</span>
                      </label>
                      <input
                        type="email"
                        id="feedback-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: '#ffffff',
                          borderColor: '#d1d5db',
                          color: '#111827',
                        }}
                      />
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <label htmlFor="feedback-message" className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>
                      Message
                    </label>
                    <textarea
                      id="feedback-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={
                        feedbackType === 'bug'
                          ? "What happened? How can we reproduce it?"
                          : feedbackType === 'feature'
                          ? "What feature would you like to see?"
                          : "What's on your mind?"
                      }
                      rows={4}
                      className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 resize-none"
                      style={{
                        backgroundColor: '#ffffff',
                        borderColor: '#d1d5db',
                        color: '#111827',
                      }}
                      required
                    />
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div
                      className="px-4 py-3 rounded-lg text-sm"
                      style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                    >
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting || !message.trim()}
                    className="w-full font-semibold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{
                      backgroundColor: submitting || !message.trim() ? '#93c5fd' : '#2563eb',
                      color: '#ffffff',
                    }}
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Send Feedback
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackButton;
